import { createContext, use } from 'react';

import type { UserRole } from '@/core/schemas';
import type { AppError } from '@/infrastructure/errors/AppError';

export interface Account {
  readonly uid: string;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly photoUrl: string | null;
  readonly role: UserRole;
}

/**
 * Modelled as a union rather than as `account: Account | null` plus an
 * `isLoading` flag: that combination permits the impossible state "loading and
 * signed in at once", and every consumer would have to remember which flag
 * wins.
 */
export type AuthSession =
  | { readonly status: 'loading' }
  | { readonly status: 'anonymous' }
  | { readonly status: 'authenticated'; readonly account: Account };

export interface AuthContextValue {
  readonly session: AuthSession;
  readonly error: AppError | null;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  dismissError: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = use(AuthContext);

  if (value === null) {
    throw new Error('useAuth muss innerhalb von <AuthProvider> verwendet werden');
  }

  return value;
}

/** Convenience for the many places that only care about the admin question. */
export function useIsAdmin(): boolean {
  const { session } = useAuth();
  return session.status === 'authenticated' && session.account.role === 'admin';
}
